/**
 * PDF receipts as page images.
 *
 * A PDF cannot go in an <img>, and a print dialog skips an embedded PDF
 * entirely — so a package of receipts would print blank wherever a receipt was
 * uploaded as a PDF. Rasterising the pages lets them sit on the page and print
 * like any photo.
 *
 * pdf.js is loaded from the CDN on first use, at the version the Reimburse
 * app already uses, rather than bundled: it is ~1 MB that only this one screen
 * needs, and nothing else in the app should pay for it.
 */

const PDFJS_BASE = "https://cdn.jsdelivr.net/npm/pdfjs-dist@4.7.76/build/";
const MAX_PX = 1600;

type PdfLib = {
  GlobalWorkerOptions: { workerSrc: string };
  getDocument: (src: { url?: string; data?: Uint8Array }) => { promise: Promise<PdfDoc> };
};
type PdfDoc = { numPages: number; getPage: (n: number) => Promise<PdfPage> };
type PdfPage = {
  getViewport: (o: { scale: number }) => { width: number; height: number };
  render: (o: { canvasContext: CanvasRenderingContext2D; viewport: unknown }) => { promise: Promise<void> };
};

let lib: Promise<PdfLib> | null = null;
function loadLib(): Promise<PdfLib> {
  if (!lib) {
    lib = import(/* @vite-ignore */ PDFJS_BASE + "pdf.min.mjs").then((mod: PdfLib) => {
      mod.GlobalWorkerOptions.workerSrc = PDFJS_BASE + "pdf.worker.min.mjs";
      return mod;
    });
    lib.catch(() => { lib = null; }); // a failed load can be retried
  }
  return lib;
}

/** The demo stores receipts as data URLs, which pdf.js wants as bytes. */
function source(url: string): { url?: string; data?: Uint8Array } {
  if (!url.startsWith("data:")) return { url };
  const bin = atob(url.slice(url.indexOf(",") + 1));
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return { data: bytes };
}

const cache = new Map<string, Promise<string[]>>();

/** Each page (up to `max`) as a JPEG data URL. Cached per URL. */
export function pdfPages(url: string, max = 12): Promise<string[]> {
  const hit = cache.get(url);
  if (hit) return hit;
  const work = (async () => {
    const pdf = await loadLib();
    const doc = await pdf.getDocument(source(url)).promise;
    const out: string[] = [];
    for (let n = 1; n <= Math.min(doc.numPages, max); n++) {
      const page = await doc.getPage(n);
      const base = page.getViewport({ scale: 1 });
      const vp = page.getViewport({ scale: Math.min(2.5, MAX_PX / base.width) });
      const cv = document.createElement("canvas");
      cv.width = Math.round(vp.width);
      cv.height = Math.round(vp.height);
      const cx = cv.getContext("2d");
      if (!cx) throw new Error("No canvas");
      await page.render({ canvasContext: cx, viewport: vp }).promise;
      out.push(cv.toDataURL("image/jpeg", 0.85));
    }
    return out;
  })();
  cache.set(url, work);
  work.catch(() => cache.delete(url));
  return work;
}
